import {
  BTypeFacilityMainHead,
  CTypeBreadCrumb,
  CTypeFacilityList,
  CTypeMap,
  CTypeTabsFacility,
  DTypeCTA,
  CTypeFacilityFAQ,
  FacilityDetailFAQ,
  DefaultFacility,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  buildFacilityPageSchema,
  GOI_WEB_URL,
  RichSearch,
  ParlorHeadSection,
  excludeFacilities,
  FacilityHiddenNotice,
  getFacilityTypeLabel,
  getFacilityDisplayName,
  getFacilityRegionKeyword,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

import facilities from "goi_common/src/constants/facilities_detail.json";

import { parlorRegion } from "@/constants/parlorRegion";
import { Stack } from "@mui/material";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((facility) => targetSidos.includes(facility.sido))
    .map((facility) => ({
      params: {
        id: String(facility.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string;

  const p_facility = facilities.find(
    (facility) => Number(facility.id) === Number(id)
  ) as DefaultFacility;

  const { address, companyname, images, type } = p_facility;
  const typeLabel = getFacilityTypeLabel(type);
  const displayName = getFacilityDisplayName(companyname, type);
  const regionKeyword = getFacilityRegionKeyword(type);

  const district = address.slice(0, 2) || "";

  const title = `(${district}) ${displayName} | 강원장례정보공개 (고이장례연구소)`;
  const description = `${district} ${regionKeyword}, ${companyname} 예약, 소개, 가격에 대해 안내해 드리겠습니다.`;
  const keywords = `${district} ${regionKeyword}, ${companyname}, ${companyname} 정보, ${companyname} 비용, ${companyname} 가격, ${companyname} 예약, ${companyname} 위치, ${companyname} 시설, ${district} 장지 비용, ${district} 공설장지, ${district} 장례 혜택`;

  return {
    props: {
      defaultFacility: p_facility,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgImage: images[0].url || "",
    },
  };
};

interface FacilityDetailPagProps {
  pageOgTitle: string;
  defaultFacility: DefaultFacility;
}

const FacilityDetailPage = ({
  pageOgTitle,
  defaultFacility,
}: FacilityDetailPagProps) => {
  const sidoName = FUNERAL_SIDO.get(Number(defaultFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultFacility.sido))?.get(
    Number(defaultFacility.sigungu)
  );

  const facilityPageSchema = buildFacilityPageSchema(defaultFacility);

  const isHidden = excludeFacilities.includes(String(defaultFacility.id));

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>

        {/* <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqList) }}
        /> */}
      </Head>
      <RichSearch data={facilityPageSchema} />
      <CTypeBreadCrumb
        firstItem={{
          text: `장지 추천`,
          href: isHidden
            ? `${GOI_WEB_URL}/facilities/search/initial/?referrerId=${defaultFacility.id}&utm_source=dogsound`
            : `${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/?utm_source=dogsound`,
          event: {
            domain: "gangwon",
            path: "facility",
            trigger_and_target: "br_crumb_mth_facil_req",
          },
        }}
        // secondItem={{
        //   text: `${sidoName} 장례식장`,
        //   href: `/parlors-list/${sidoName} 의 장례식장 목록/`,
        //   event: {
        //     domain: "gangwon",
        //     path: "prlr_item_intro",
        //     trigger_and_target: "br_crumb_sido_prlr_list",
        //   },
        // }}
        // thirdItem={{
        //   text: `${sigunguName} 장례식장`,
        //   href: `/parlors-list/${sidoName}-${sigunguName} 의 장례식장 목록/`,
        //   event: {
        //     domain: "gangwon",
        //     path: "prlr_item_intro",
        //     trigger_and_target: "br_crumb_sigungu_prlr_list",
        //   },
        // }}
        goiHomeEvent={{
          domain: "gangwon",
          path: "facility",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <ParlorHeadSection
        component={
          <>
            <BTypeFacilityMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              defaultFacility={defaultFacility}
              title={`${defaultFacility.companyname}`}
              type="jangji"
            />
          </>
        }
      />
      {isHidden ? (
        <FacilityHiddenNotice
          companyname={defaultFacility.companyname}
          mainColor={regionColor}
          ctaHref={`${GOI_WEB_URL}/facilities/search/initial/?referrerId=${defaultFacility.id}&utm_source=dogsound`}
          event={{
            domain: "gangwon",
            path: "facility",
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      ) : (
        <>
      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        <DTypeCTA
          sidoName={sidoName}
          sigunguName={sigunguName}
          mainColor={regionColor}
          text="장지 비용 알아보기"
          companyname={defaultFacility.companyname}
          href={`${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/?utm_source=dogsound`}
          event={{
            domain: "gangwon",
            path: "facility",
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      </Stack>

      <Stack style={{ background: "white" }}>
        <CTypeTabsFacility
          mainColor={regionColor}
          href={`parlor-item/${sidoName} ${sigunguName} 의 ${defaultFacility.companyname}`}
          selectTab="소개"
          event={{
            domain: "gangwon",
            path: "facility",
            trigger_and_target: "CType_facil_tab",
          }}
        />
      </Stack>

      <Stack padding={"40px 20px"}>
        <CTypeFacilityFAQ
          event={{
            domain: "gangwon",
            path: "facility",
          }}
        />
        <FacilityDetailFAQ />
      </Stack>

      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        <CTypeMap defaultParlor={defaultFacility} />
      </Stack>
        </>
      )}

      <Stack
        style={{
          padding: "40px 20px",
        }}
      >
        <CTypeFacilityList
          sido={defaultFacility.sido}
          title={`${sidoName}의 다른 장지`}
          defaultHref={"/gangwon-facility"}
          mainColor={regionColor}
          event={{
            domain: "gangwon",
            path: "facility",
            trigger_and_target: "uv_facility",
          }}
        />
      </Stack>
    </>
  );
};

export default FacilityDetailPage;
